import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  TrendingUp, 
  AlertTriangle, 
  HelpCircle, 
  Compass,
  ArrowRight,
  CheckCircle2,
  Circle,
  FileText,
  Bookmark,
  Scale,
  Search,
  Users
} from 'lucide-react';
import { beslutslageTranslations } from '../../lib/beslutslageTranslations';
import { AUTH_ENABLED, SALES_ENABLED } from '../../lib/featureFlags';
import { MaintenanceNotice } from '../common/MaintenanceNotice';

/**
 * BeslutslageResults - Fastighetsvärde Results Page Structure
 * 
 * Skogskalkyl = von der Zahl zur Entscheidung
 * (Starts with calculation result, leads to decision insight)
 * 
 * Blocks 1-5 (Gratis):
 * 1. Resultat - huvudvärde, intervall, kort förklaring
 * 2. Vad det betyder - kurze Einordnung
 * 3. Hur säkert - Unsicherheitsgrad, Annahmen
 * 4. Vad som saknas - 3-5 wichtigste fehlende Angaben
 * 5. Beslutsriktning - Richtungsoptionen
 * 
 * Block 6 (Premium Teaser):
 * 6. Beslutsläge-Teaser
 * 
 * Block 7 (Registrerad):
 * 7. Spara/återkom
 */

// ============================================
// BLOCK 1: RESULTAT
// ============================================
export const ResultatBlock = ({ result, inputs, language = 'sv' }) => {
  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  const r = t.resultat;
  
  if (!result) return null;

  // Generate short explanation based on inputs
  const getShortExplanation = () => {
    const region = inputs?.region || 'svealand';
    const regionNames = {
      sv: { norrland: 'norra Sverige', svealand: 'mellersta Sverige', gotaland: 'södra Sverige' },
      de: { norrland: 'Nordschweden', svealand: 'Mittelschweden', gotaland: 'Südschweden' },
      en: { norrland: 'northern Sweden', svealand: 'central Sweden', gotaland: 'southern Sweden' }
    };
    const names = regionNames[language] || regionNames.sv;
    
    if (language === 'sv') {
      return `Värdet baseras på ${inputs?.areal || 50} ha produktiv mark med ${(inputs?.virkesvolym || 7500).toLocaleString('sv-SE')} m³sk i ${names[region]}.`;
    }
    return `Value based on ${inputs?.areal || 50} ha with ${(inputs?.virkesvolym || 7500).toLocaleString()} m³ in ${names[region]}.`;
  };

  return (
    <Card className="border-forest-300 bg-gradient-to-br from-forest-50 to-white shadow-sm" data-testid="block-resultat">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-forest-800 text-lg">
            <TrendingUp className="h-5 w-5" />
            {r.title}
          </CardTitle>
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
            {t.tiers.free}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Value - huvudvärde */}
        <div className="text-center py-4 bg-white rounded-lg border border-forest-100">
          <p className="text-sm text-muted-foreground mb-1">{r.estimate}</p>
          <p className="text-4xl font-bold text-forest-700 font-mono tracking-tight" data-testid="main-value">
            {result.midEstimate?.toLocaleString('sv-SE')} kr
          </p>
        </div>
        
        {/* Range - intervall/spann */}
        <div className="text-center py-3 bg-forest-50/50 rounded-lg">
          <p className="text-sm text-muted-foreground">{r.range}</p>
          <p className="text-lg font-semibold text-forest-600" data-testid="value-range">
            {result.lowEstimate?.toLocaleString('sv-SE')} – {result.highEstimate?.toLocaleString('sv-SE')} kr
          </p>
        </div>
        
        {/* Short explanation - kort förklaring */}
        <p className="text-sm text-muted-foreground text-center">
          {getShortExplanation()}
        </p>
      </CardContent>
    </Card>
  );
};

// ============================================
// BLOCK 2: VAD DET BETYDER
// ============================================
export const InterpretationBlock = ({ result, inputs, language = 'sv' }) => {
  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  const i = t.interpretation;
  
  if (!result) return null;
  
  const areal = inputs?.areal || 50;
  const pricePerHa = result.pricePerHa || (result.midEstimate / areal);
  
  // Regional benchmarks (kr per ha)
  const benchmarks = {
    norrland: 65000,
    svealand: 85000,
    gotaland: 110000
  };
  const benchmark = benchmarks[inputs?.region] || benchmarks.svealand;
  
  const isAbove = pricePerHa > benchmark * 1.1;
  const isBelow = pricePerHa < benchmark * 0.9;
  
  // Value driver
  const volymPerHa = (inputs?.virkesvolym || 7500) / areal;
  const isTimberDriven = volymPerHa > 150;

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50/30 to-white shadow-sm" data-testid="block-interpretation">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-blue-800 text-lg">
            <Compass className="h-5 w-5" />
            {i.title}
          </CardTitle>
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
            {t.tiers.free}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Regional comparison badge */}
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
            isAbove ? 'bg-green-100 text-green-700 border border-green-200' : 
            isBelow ? 'bg-amber-100 text-amber-700 border border-amber-200' : 
            'bg-gray-100 text-gray-700 border border-gray-200'
          }`}>
            {isAbove ? <TrendingUp className="h-4 w-4" /> : isBelow ? <TrendingUp className="h-4 w-4 rotate-180" /> : <Scale className="h-4 w-4" />}
            {isAbove ? i.aboveAverage : isBelow ? i.belowAverage : i.atAverage}
          </span>
        </div>
        
        {/* Short interpretation */}
        <p className="text-sm text-gray-700 leading-relaxed">
          {isTimberDriven ? i.timberDriven : i.landDriven}
        </p>
      </CardContent>
    </Card>
  );
};

// ============================================
// BLOCK 3: HUR SÄKERT
// ============================================
export const UncertaintyBlock = ({ result, inputs, language = 'sv' }) => {
  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  const u = t.uncertainty;
  
  // Calculate uncertainty level based on input quality
  const hasForestPlan = inputs?.hasForestPlan || false;
  const hasVerifiedVolume = inputs?.hasVerifiedVolume || false;
  
  let level = 'medium';
  if (hasForestPlan && hasVerifiedVolume) level = 'low';
  if (!hasForestPlan && !hasVerifiedVolume) level = 'high';
  
  const levelConfig = {
    low: { color: 'bg-green-100 text-green-700 border-green-300', label: u.low },
    medium: { color: 'bg-amber-100 text-amber-700 border-amber-300', label: u.medium },
    high: { color: 'bg-red-100 text-red-700 border-red-300', label: u.high }
  };
  
  // Decision basis hint
  const getBasisHint = () => {
    if (language === 'sv') {
      if (level === 'low') return 'Kan fungera som beslutsunderlag för enklare beslut.';
      if (level === 'medium') return 'Ger en orientering – men fördjupa gärna innan större beslut.';
      return 'Endast för första orientering – kräver mer underlag för beslut.';
    }
    if (level === 'low') return 'May serve as basis for simpler decisions.';
    if (level === 'medium') return 'Provides orientation – but deepen before major decisions.';
    return 'For initial orientation only – requires more data for decisions.';
  };

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50/30 to-white shadow-sm" data-testid="block-uncertainty">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-amber-800 text-lg">
            <AlertTriangle className="h-5 w-5" />
            {u.title}
          </CardTitle>
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
            {t.tiers.free}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Uncertainty level indicator */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{u.level}:</span>
          <span className={`px-3 py-1 rounded-full border text-sm font-medium ${levelConfig[level].color}`}>
            {levelConfig[level].label}
          </span>
        </div>
        
        {/* Key assumptions */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">{u.factors}</p>
          <ul className="space-y-1.5">
            <li className="flex items-start gap-2 text-sm text-gray-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              {u.volumeNotVerified}
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              {u.priceRegional}
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
              {u.marketFactors}
            </li>
          </ul>
        </div>
        
        {/* Decision basis hint */}
        <p className="text-sm text-amber-700 italic border-l-2 border-amber-300 pl-3 py-1 bg-amber-50/50 rounded-r">
          {getBasisHint()}
        </p>
      </CardContent>
    </Card>
  );
};

// ============================================
// BLOCK 4: VAD SOM SAKNAS
// ============================================
export const MissingBlock = ({ inputs, language = 'sv' }) => {
  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  const m = t.missing;

  return (
    <Card className="border-slate-200 bg-gradient-to-br from-slate-50/30 to-white shadow-sm" data-testid="block-missing">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-slate-700 text-lg">
            <HelpCircle className="h-5 w-5" />
            {m.title}
          </CardTitle>
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
            {t.tiers.free}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">{m.intro}</p>
        
        <ul className="space-y-2">
          <li className="flex items-start gap-3 text-sm text-gray-700">
            <span className="w-5 h-5 rounded border border-slate-300 flex-shrink-0 mt-0.5 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-slate-200" />
            </span>
            {m.inventory}
          </li>
          <li className="flex items-start gap-3 text-sm text-gray-700">
            <span className="w-5 h-5 rounded border border-slate-300 flex-shrink-0 mt-0.5 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-slate-200" />
            </span>
            {m.forestPlan}
          </li>
          <li className="flex items-start gap-3 text-sm text-gray-700">
            <span className="w-5 h-5 rounded border border-slate-300 flex-shrink-0 mt-0.5 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-slate-200" />
            </span>
            {m.marketInterest}
          </li>
          <li className="flex items-start gap-3 text-sm text-gray-700">
            <span className="w-5 h-5 rounded border border-slate-300 flex-shrink-0 mt-0.5 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-slate-200" />
            </span>
            {m.localAssessment}
          </li>
        </ul>
      </CardContent>
    </Card>
  );
};

// ============================================
// BLOCK 5: BESLUTSRIKTNING
// ============================================
export const DirectionBlock = ({ result, inputs, language = 'sv' }) => {
  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  const d = t.direction;
  
  const pricePerHa = result?.pricePerHa || (result?.midEstimate / (inputs?.areal || 50));
  const benchmark = { norrland: 65000, svealand: 85000, gotaland: 110000 }[inputs?.region] || 85000;
  
  // Highlight most relevant option based on value position
  const isHighValue = pricePerHa > benchmark * 1.1;

  const options = [
    { 
      key: 'keep', 
      icon: Bookmark, 
      highlighted: !isHighValue 
    },
    { 
      key: 'compare', 
      icon: Scale, 
      highlighted: true 
    },
    { 
      key: 'deepen', 
      icon: Search, 
      highlighted: true 
    },
    { 
      key: 'external', 
      icon: Users, 
      highlighted: isHighValue 
    }
  ];

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50/20 to-white shadow-sm" data-testid="block-direction">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-purple-800 text-lg">
            <Compass className="h-5 w-5" />
            {d.title}
          </CardTitle>
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
            {t.tiers.free}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">{d.intro}</p>
        
        {/* Direction options */}
        <div className="space-y-2">
          {options.map(({ key, icon: Icon, highlighted }) => (
            <div 
              key={key}
              className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                highlighted 
                  ? 'bg-purple-50 border border-purple-200' 
                  : 'bg-gray-50 border border-transparent'
              }`}
            >
              {highlighted ? (
                <CheckCircle2 className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              ) : (
                <Circle className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className={`font-medium text-sm ${highlighted ? 'text-purple-800' : 'text-gray-600'}`}>
                  {d.options[key].title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {d.options[key].desc}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Recommendation */}
        <p className="text-sm text-purple-700 italic border-l-2 border-purple-300 pl-3 py-1 bg-purple-50/50 rounded-r">
          {d.recommendation}
        </p>
      </CardContent>
    </Card>
  );
};

// ============================================
// BLOCK 6: BESLUTSLÄGE TEASER (Premium)
// ============================================
export const BeslutslageTeaser = ({ language = 'sv', isComingSoon = false, onActivate }) => {
  // Wartemodus: never render the Premium teaser in public UI while sales are paused.
  if (!SALES_ENABLED) return null;

  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  const p = t.premium;

  return (
    <Card 
      className="border-2 border-forest-300 bg-gradient-to-br from-forest-50 via-white to-amber-50/20 shadow-md relative overflow-hidden"
      data-testid="block-beslutslage-teaser"
    >
      {/* Subtle decorative element */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-forest-100/50 to-transparent rounded-bl-full" />
      
      <CardHeader className="pb-3 relative">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-forest-800 text-lg">
            <FileText className="h-5 w-5" />
            {p.title}
          </CardTitle>
          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
            {language === 'sv' ? 'Premium' : 'Premium'}
          </span>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-5 relative">
        {/* Question */}
        <p className="text-base font-medium text-forest-800">
          {p.question}
        </p>
        
        {/* Intro */}
        <p className="text-sm text-forest-700">
          {p.intro}
        </p>
        
        {/* Feature list */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-forest-700">{p.includes}</p>
          <ul className="space-y-2">
            {p.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-forest-600">
                <CheckCircle2 className="h-4 w-4 text-forest-500 mt-0.5 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Visual separator - Gratis/Premium boundary */}
        <div className="flex items-center gap-3 py-2">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-forest-200 to-transparent" />
          <span className="text-xs text-muted-foreground px-2">
            {language === 'sv' ? 'Fördjupning' : 'Deep dive'}
          </span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-forest-200 to-transparent" />
        </div>
        
        {/* CTA */}
        <div className="space-y-3">
          {!SALES_ENABLED ? (
            <MaintenanceNotice testId="beslutslage-teaser-maintenance-notice" />
          ) : isComingSoon ? (
            <>
              <Button 
                disabled
                className="w-full bg-gray-400 cursor-not-allowed"
                data-testid="beslutslage-cta-disabled"
              >
                {p.comingSoon}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {p.oneTime} · {p.price}
              </p>
            </>
          ) : (
            <>
              <Button 
                onClick={onActivate}
                className="w-full bg-forest-700 hover:bg-forest-800 text-white"
                data-testid="beslutslage-cta"
              >
                {p.cta} – {p.price}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {p.oneTime}
              </p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// BLOCK 7: SPARA / ÅTERKOM
// ============================================
export const SaveBlock = ({ 
  language = 'sv', 
  isLoggedIn = false,
  onSave,
  onCompare,
  isSaved = false
}) => {
  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  const s = t.save;

  // Wartemodus: hide the SaveBlock entirely for non-logged-in users while
  // AUTH_ENABLED is false. Logged-in users (legacy sessions) keep save/compare.
  if (!isLoggedIn && !AUTH_ENABLED) {
    return null;
  }

  return (
    <Card className="border-gray-200 bg-gray-50/50 shadow-sm" data-testid="block-save">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-gray-700 text-base">
            <Bookmark className="h-4 w-4" />
            {s.title}
          </CardTitle>
          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
            {t.tiers.registered}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-2">
          {isLoggedIn ? (
            <>
              <Button 
                variant="outline" 
                className="flex-1 border-forest-300 text-forest-700 hover:bg-forest-50"
                onClick={onSave}
                disabled={isSaved}
                data-testid="save-calculation-btn"
              >
                <Bookmark className="mr-2 h-4 w-4" />
                {isSaved ? s.saved : s.saveBtn}
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 border-gray-300"
                onClick={onCompare}
                data-testid="compare-btn"
              >
                {s.compareBtn}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              className="w-full border-gray-300 text-gray-600 hover:bg-gray-100"
              onClick={() => window.location.href = '/register'}
              data-testid="login-to-save-btn"
            >
              {s.loginRequired}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default {
  ResultatBlock,
  InterpretationBlock,
  UncertaintyBlock,
  MissingBlock,
  DirectionBlock,
  BeslutslageTeaser,
  SaveBlock
};
